﻿import React, { useContext } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, makeStyles, Typography, useMediaQuery } from '@material-ui/core';

import { ATTACHMENT_SUPPORTED_FORMATS } from '../../constants';
import FileUploadContext from './FileUploadContext';
import UploadIcon from '../svg/UploadIcon';

const useStyles = makeStyles((theme) => ({
  container: {
    [theme.breakpoints.up('sm')]: {
      height: theme.spacing(17),
    },
    backgroundColor: theme.palette.common.polar,
    padding: theme.spacing(3),
    border: `1px dashed ${theme.palette.common.leaf}`,
    borderRadius: 8,
    '& a': {
      color: theme.palette.primary.main,
      fontWeight: 500,
      textDecoration: 'none',
    },
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      padding: '10px',
      borderWidth: 2,
      borderColor: theme.palette.common.softCyan,
      borderStyle: 'solid',
      background: 'none',
      outline: 'none',
      transition: 'border .24s ease-in-out',
      cursor: 'pointer',
      borderRadius: 8,
    },
  },
  paragraph2: {
    paddingTop: theme.spacing(1),
    color: theme.palette.grey[1000],
    fontWeight: '400',
  },
  uploadText: {
    [theme.breakpoints.up('sm')]: {
      fontWeight: '400',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: 14,
    },
  },
  containerBox: {
    maxWidth: theme.spacing(24.75),
  },
  centerText: {
    textAlign: 'center',
  },
}));

const DropzoneFileUpload = (props) => {
  const { onFilesSelected, onFileStatusChange, onError, ...other } = props;
  const {
    maxFilesCount,
    maxFileSizeInMb,
    onFileUpload,
    fileGroupId,
    gcAccountId,
    canUpload,
    fileCount,
  } = useContext(FileUploadContext);

  const classes = useStyles();

  const handleFilesAccepted = (files) => {
    const filesToUpload = [];

    let newFileCount = fileCount + 1;

    for (let i = 0; i < files.length; i++) {
      if (maxFilesCount && newFileCount > maxFilesCount) {
        onError(`A maximum of ${maxFilesCount} files is allowed`);
        break;
      }

      const file = files[i];

      if (canUpload && !canUpload(file)) {
        return;
      }

      filesToUpload.push(file);
      newFileCount++;
    }

    if (onFilesSelected) {
      onFilesSelected(
        filesToUpload.map((file) => ({
          name: file.name,
          type: file.type,
          size: file.size,
          error: '',
          uploaded: false,
          timestamp: new Date(),
        })),
      );
    }

    filesToUpload.forEach((file) => {
      onFileUpload(gcAccountId, fileGroupId, file).then((response) => {
        if (!response) return;
        const { error, documentFileId } = response;
        onFileStatusChange({
          type: file.type,
          name: file.name,
          size: file.size,
          error: error ? 'Upload failed' : '',
          uploaded: !error,
          timestamp: new Date(),
          fileId: documentFileId,
        });
      });
    });
  };

  const handleFilesRejected = (files) => {
    if (onFilesSelected) {
      onFilesSelected(
        files.map(({ file, errors }) => ({
          name: file.name,
          type: file.type,
          size: file.size,
          error:
            errors[0].code === 'file-too-large'
              ? `The maximum file size allowed is ${maxFileSizeInMb}MB`
              : errors[0].message,
          uploaded: false,
          timestamp: new Date(),
        })),
      );
    }
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    accept: ATTACHMENT_SUPPORTED_FORMATS.join(','),
    maxFilesCount: 2,
    maxSize: 10 * 1048576,
    noClick: true,
    noKeyboard: true,
    onDropAccepted: handleFilesAccepted,
    onDropRejected: handleFilesRejected,
  });

  const handleBrowseFiles = (e) => {
    e.preventDefault();
    open();
  };

  const isMobile = useMediaQuery('(max-width:600px)');
  const uploadText = isMobile ? '' : 'Drag-and-drop your files or ';
  const browseText = isMobile ? 'Upload files' : 'browse';
  const maxFileText = isMobile
    ? ''
    : `Upload files in PDF, JPG or PNG format. Max file size ${maxFileSizeInMb} MB.`;

  return (
    <Box className={classes.container} {...getRootProps()} data-testid={props.dataTestid}>
      <input {...getInputProps()} />
      <Box className={classes.containerBox}>
        {!isMobile && (
          <Box className={classes.centerText}>{props.icon ? props.icon : <UploadIcon />}</Box>
        )}
        <Box className={classes.uploadTextBox} pt={1}>
          <Typography variant="h6" className={classes.uploadText}>
            {uploadText}
            <a href={null} onClick={handleBrowseFiles} data-testid={`${props.dataTestid}-browse`}>
              {browseText}
            </a>
          </Typography>
        </Box>
        {!isMobile && (
          <Box className={classes.uploadFileTextContainer}>
            <Typography variant="h6" style={{}} className={classes.paragraph2}>
              {props.uploadFileText ? props.uploadFileText : maxFileText}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DropzoneFileUpload;
